import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { UserProfile, UserRole } from '../../../shared/models/auth.model';
import { AuthService } from '../../../core/services/auth.service';
import { UsersService } from '../../../shared/services/users.service';

interface UserTypeOption {
  label: string;
  value: string;
}

interface RoleResponse {
  id: string;
  name: string;
  isActive: boolean;
}

@Component({
  selector: 'app-users-create',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    SelectModule,
    CheckboxModule,
    ButtonModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './users-create.component.html',
  styleUrl: './users-create.component.scss',
})
export class UsersCreateComponent implements OnInit {
  // User profile for header
  userProfile: UserProfile | null = null;

  // Form
  userForm: FormGroup;
  isSubmitting: boolean = false;
  showPassword: boolean = false;

  // User type options - will be loaded from API
  userTypeOptions: UserTypeOption[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private usersService: UsersService,
    private messageService: MessageService
  ) {
    this.userForm = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(50),
          Validators.pattern(/^[a-zA-Z\s]+$/),
        ],
      ],
      email: [
        '',
        [Validators.required, Validators.email, Validators.maxLength(100)],
      ],
      password: ['', Validators.required],
      userType: ['', Validators.required],
      isActive: [true],
      isNonActive: [false],
    });
  }

  ngOnInit(): void {
    // Component initialization
    this.checkUserRole();
    this.loadUserTypeOptions();
  }

  private checkUserRole(): void {
    const currentUser = this.authService.getCurrentUser();

    // Set userProfile from authenticated user
    if (currentUser) {
      this.userProfile = {
        id: currentUser.email,
        name: currentUser.name,
        email: currentUser.email,
        role: currentUser.role,
        avatar: 'assets/images/logo.png',
      };
    }
  }

  private loadUserTypeOptions(): void {
    // Load user type options from API
    this.usersService.getActiveRoles().subscribe({
      next: (roles) => {
        this.userTypeOptions = roles;
      },
      error: (error) => {
        // Show "No Data Found" if API fails
        this.userTypeOptions = [{ label: 'No Data Found', value: 'no-data' }];
      },
    });
  }

  // Helper method to check if a field has errors
  isFieldInvalid(fieldName: string): boolean {
    const field = this.userForm.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  // Helper method to get field error message
  getFieldError(fieldName: string): string {
    const field = this.userForm.get(fieldName);
    if (!field || !field.errors || !(field.dirty || field.touched)) {
      return '';
    }

    const errors = field.errors;

    if (errors['required']) {
      return this.getRequiredErrorMessage(fieldName);
    }
    if (errors['minlength']) {
      return `${this.getFieldLabel(fieldName)} must be at least ${
        errors['minlength'].requiredLength
      } characters long.`;
    }
    if (errors['maxlength']) {
      return `${this.getFieldLabel(fieldName)} must not exceed ${
        errors['maxlength'].requiredLength
      } characters.`;
    }
    if (errors['email']) {
      return 'Please enter a valid email address.';
    }
    if (errors['pattern']) {
      if (fieldName === 'name') {
        return 'Name can only contain letters and spaces.';
      }
      return `${this.getFieldLabel(fieldName)} contains invalid characters.`;
    }

    return `${this.getFieldLabel(fieldName)} is invalid.`;
  }

  // Helper method to get required error message
  private getRequiredErrorMessage(fieldName: string): string {
    const fieldLabels: { [key: string]: string } = {
      name: 'Name is required',
      email: 'Email address is required',
      password: 'Password is required',
      userType: 'User type is required',
    };
    return (
      fieldLabels[fieldName] || `${this.getFieldLabel(fieldName)} is required`
    );
  }

  // Helper method to get field label
  private getFieldLabel(fieldName: string): string {
    const fieldLabels: { [key: string]: string } = {
      name: 'Name',
      email: 'Email address',
      password: 'Password',
      userType: 'User type',
    };
    return fieldLabels[fieldName] || fieldName;
  }

  // Password visibility toggle
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  // Active status change handler
  onActiveStatusChange(value: boolean): void {
    if (value) {
      // Yes selected - uncheck No
      this.userForm.patchValue({ isNonActive: false });
    } else {
      // No selected - uncheck Yes
      this.userForm.patchValue({ isActive: false });
    }
  }

  // Form submission
  onSubmit(): void {
    if (this.userForm.valid) {
      this.isSubmitting = true;

      // Get form values
      const formValue = this.userForm.value;

      // Find the selected role name from userTypeOptions
      const selectedRole = this.userTypeOptions.find(
        (option) => option.value === formValue.userType
      );
      const roleName = selectedRole ? selectedRole.label : '';

      // Create payload with only the required fields
      const payload = {
        email: formValue.email,
        password: formValue.password,
        fullName: formValue.name,
        roleName: roleName,
        isActive: formValue.isActive,
      };

      // Call the API
      this.usersService.createUser(payload).subscribe({
        next: (response) => {
          this.isSubmitting = false;
          
          // Show success toast
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'User added successfully',
            life: 3000,
          });
          
          // Navigate back to users list after a delay to show the confirmation message
          setTimeout(() => {
            this.router.navigate(['/users']);
          }, 2000);
        },
        error: (error) => {
          this.isSubmitting = false;

          // Handle different error types with toast messages
          let errorMessage = 'Error creating user. Please try again.';

          // Extract error message from response
          if (error.error && error.error.message) {
            errorMessage = error.error.message;
          } else if (error.message) {
            errorMessage = error.message;
          }

          this.messageService.clear(); // Clear any existing messages
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
            life: 5000,
          });
        },
      });
    } else {
      // Mark all fields as touched to trigger validation display
      Object.keys(this.userForm.controls).forEach((key) => {
        const control = this.userForm.get(key);
        control?.markAsTouched();
      });
    }
  }

  // Navigation
  goBack(): void {
    this.router.navigate(['/users']);
  }
}
